import {
  booleanAttribute,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GalaxyImageUploaderService, ImageDimensions } from '../../image-uploader.service';
import { FileInfo, FileUploadError, GALAXY_UPLOADER_SERVICE_TOKEN } from '../../uploader.interface';
import { GalaxyUploaderComponent } from '../uploader/uploader.component';

@Component({
  selector: 'glxy-image-uploader',
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GalaxyImageUploaderComponent implements OnInit, OnChanges {
  @HostBinding('class') class = 'glxy-image-uploader';

  /**
   * Comma separated file types to accept
   * See MDN on File Type Specifiers https://mzl.la/3gytrGi
   */
  @Input() accept = 'image/*';

  /** Whether or not to upload files when they are added to the list */
  @Input({ transform: booleanAttribute }) autoUpload = false;

  /** Choose the the layout of the component */
  @Input() layout: 'default' | 'horizontal' | 'button-only' = 'default';

  /**
   * Max number of files allowed to upload
   * 0 means no limit
   */
  @Input() maxFiles?: number;

  /** If provided, will not allow images larger than the size provided to be added to the queue. Bytes. */
  @Input() maxFileSize?: number;

  /** If provided, will not allow images larger than the dimensions provided to be added to the queue */
  @Input() maxDimensions?: ImageDimensions;

  /** If provided, will not allow images smaller than the dimensions provided to be added to the queue */
  @Input() minDimensions?: ImageDimensions;

  /** If provided, will be used as the hint instead of the file constraints text */
  @Input() hintText?: string;

  /** If provided, will override the button text. First item is singular, second is plural */
  @Input() buttonText: [string, string] = ['GALAXY.UPLOADER.CHOOSE_IMAGE', 'GALAXY.UPLOADER.CHOOSE_IMAGE_PL'];

  /** If provided, will override the desription text. First item is singular, second is plural */
  @Input() descriptionText: [string, string] = [
    'GALAXY.UPLOADER.DRAG_AND_DROP_IMAGE',
    'GALAXY.UPLOADER.DRAG_AND_DROP_IMAGE_PL',
  ];

  /** If provided, color the uploader border red to imply uploader has an error */
  @Input({ transform: booleanAttribute }) hasError = false;

  /** URL to upload files to */
  @Input() uploadUrl!: string;

  /**
   * Number of parallel uploads to allow
   * 0 means no limit
   */
  @Input() numParallel?: number = 10;

  /** Base set of files that the service should manage */
  @Input() files: FileInfo[] = [];

  /**
   * Emits events related to the status of uploads and file selection
   */
  @Output() fileUploadErrored: EventEmitter<FileUploadError> = new EventEmitter();

  /**
   * Emits info for files uploaded successfully
   */
  @Output() fileUploaded: EventEmitter<FileInfo> = new EventEmitter();

  /**
   * Emits the list of files and their status when changes occur
   */
  @Output() filesChanged: EventEmitter<FileInfo[]> = new EventEmitter();

  @ViewChild(GalaxyUploaderComponent) uploadComp?: GalaxyUploaderComponent;

  currentFile?: FileInfo;

  constructor(@Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploadService: GalaxyImageUploaderService) {}

  ngOnInit(): void {
    if (this.maxDimensions) this.uploadService.setMaxDimensions(this.maxDimensions);
    if (this.minDimensions) this.uploadService.setMinDimensions(this.minDimensions);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.maxDimensions && changes.maxDimensions.previousValue !== changes.maxDimensions.currentValue) {
      if (this.maxDimensions) this.uploadService.setMaxDimensions(this.maxDimensions);
    }
    if (changes.minDimensions && changes.minDimensions.previousValue !== changes.minDimensions.currentValue) {
      if (this.minDimensions) this.uploadService.setMinDimensions(this.minDimensions);
    }
  }

  /**
   * Trigger all images in the queue to be uploaded
   */
  public uploadQueuedFiles(): void {
    this.uploadService.uploadQueuedFiles();
  }

  public deleteFile(file: FileInfo): void {
    this.uploadService.removeFile(file);
  }
}
